import { useEffect, useMemo, useState, useRef } from "react";
import { Card } from "primereact/card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { Skeleton } from "primereact/skeleton";

import useAccessToken from "../Custom/useAccessToken";
import { getExperienceDetails } from "../Services/GetService";

interface LBMCExperienceApiRow {
  clientName: string;
  clientGroup: string;
  projectType: string;
  taskName: string;
}

interface ProfileCardDashboardProps {
  profileID?: string | null;
  isOwnProfile?: boolean;
}

const ProfessionalExperience: React.FC<ProfileCardDashboardProps> = ({
  profileID,
  isOwnProfile
}) => {
  const { accessToken } = useAccessToken();
  const dtRef = useRef<any>(null);
  const [experiences, setExperiences] = useState<LBMCExperienceApiRow[]>([]);
  const [globalFilter, setGlobalFilter] = useState<string>("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchExperiences = async () => {
      if (!accessToken || !profileID) return;
      setLoading(true);
      try {
        const experiences: LBMCExperienceApiRow[] = await getExperienceDetails(
          accessToken,
          profileID
        );
        setExperiences(experiences);
        setGlobalFilter("");
      } catch (err) {
        console.error("Failed to fetch experiences:", err);
        setExperiences([]);
        setGlobalFilter("");
      }
      setLoading(false);
    };

    fetchExperiences();
  }, [accessToken, profileID]);

  const filteredExperiences = useMemo(() => {
    if (!globalFilter) return experiences;
    const q = globalFilter.trim().toLowerCase();
    return experiences.filter((row) => {
      return (
        (row.clientName || "").toLowerCase().includes(q) ||
        (row.clientGroup || "").toLowerCase().includes(q) ||
        (row.projectType || "").toLowerCase().includes(q) ||
        (row.taskName || "").toLowerCase().includes(q)
      );
    });
  }, [experiences, globalFilter]);

  const rowsPerPage = 25;
  const showPaginator = filteredExperiences.length > rowsPerPage;

  return (
    <>     
      <Card
        className="mb-3 shadow-1"
        title={
          <span className="text-title font-semibold mb-3">
            LBMC Professional Experience
          </span>
        }
      >
        <div className="w-full overflow-x-auto">
          <div className="mb-3">
            <div className="flex items-center gap-2 p-1">
              <div className="flex-1">
                <IconField iconPosition="left" className="h-full">
                  <InputIcon className="pi pi-search" />
                  <InputText
                    value={globalFilter}
                    onChange={(e) => {
                      setGlobalFilter(e.target.value);
                    }}
                    placeholder="Filter Professional Experience"
                    className="p-inputtext p-component p-inputtext-sm w-full h-10"
                  />
                </IconField>
              </div>
              <Button
                type="button"
                className="p-button-outlined p-button p-component p-button-sm h-10"
                icon="pi pi-filter-slash"
                label="Clear All"
                onClick={() => {
                  setGlobalFilter("");
                  dtRef.current?.reset();
                }}
              />
            </div>
          </div>
          {loading ? (
            <div>
              {["skeleton-a", "skeleton-b"].map((id) => (
                <div
                  key={id}
                  className="p-3 flex items-center justify-between border-round surface-card mb-2"
                >
                  <div className="flex items-center">
                    <Skeleton shape="circle" size="48px" className="mr-3" />
                    <div>
                      <Skeleton width="220px" height="1.2rem" className="mb-2" />
                      <Skeleton width="160px" height="1rem" />
                    </div>
                  </div>
                  <div>
                    <Skeleton width="120px" height="1rem" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <DataTable
              ref={dtRef}
              value={filteredExperiences}
              size="small"
              paginator={showPaginator}
              rows={rowsPerPage}
              paginatorClassName="custom-paginator"
              sortMode="multiple"
              scrollable
              scrollHeight="300px"
            >
              <Column
                field="clientName"
                header="Client"
                sortable
                style={{ minWidth: "14rem" }}
                headerClassName="text-sm"
                className="text-sm"
              />
              <Column
                field="projectType"
                header="Project Type"
                sortable
                style={{ minWidth: "14rem" }}
                headerClassName="text-sm column-border-left"
                className="text-sm"
              />
            </DataTable>
          )}
        </div>
      </Card>
    </>
  );
};

export default ProfessionalExperience;
